import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Dictionary.scss';
import { useTheme } from '../hooks/useTheme';
import { fetchDictionaryCategories, DictionaryCategory, Word, WordStatus, updateWordStatus, searchWords } from '../utils/api';
import WordsList, { WordItem } from '../components/WordsList';
import WordCard from '../components/WordCard';
import CreateCategoryModal from '../components/CreateCategoryModal';
import GenerateCategoryModal from '../components/GenerateCategoryModal';
import alreadyLearnedIconLight from '../assets/light/alreadyLearnedIcon.svg';
import learningIconLight from '../assets/light/learningIcon.svg';
import blackListIconLight from '../assets/light/blackListIcon.svg';
import searchIconLight from '../assets/light/SearchIcon.svg';
import alreadyLearnedIconDark from '../assets/dark/alreadyLearnedIcon.svg';
import learningIconDark from '../assets/dark/learningIcon.svg';
import blackListIconDark from '../assets/dark/blackListIcon.svg';
import searchIconDark from '../assets/dark/SearchIcon.svg';
import addIconLight from '../assets/light/addIcon.svg';
import addIconDark from '../assets/dark/addIcon.svg';
import generateCategoryIconLight from '../assets/light/generateCategoryIcon.svg';
import generateCategoryIconDark from '../assets/dark/generateCategoryIcon.svg';
import logger from '../utils/logger';

interface WordCategory {
  emoji: string;
  name: string;
  id?: string; 
}

interface Preset {
  name: string;
  id: string;
  icon: string;
}

interface SearchResponse {
  content: Word[];
  totalPages: number;
  totalElements: number;
  pageable: {
    pageNumber: number;
  };
}

const Dictionary = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<WordCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<WordItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedWord, setSelectedWord] = useState<Word | null>(null);
  const [highlightedWords, setHighlightedWords] = useState<Set<string>>(new Set());
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isAddCategoryModalOpen, setIsAddCategoryModalOpen] = useState(false);
  const [isGenerateCategoryModalOpen, setIsGenerateCategoryModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const searchSectionRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const navigate = useNavigate();
  const { theme } = useTheme();

  const presets: Preset[] = [
    { 
      name: "Знаю", 
      id: "known", 
      icon: theme === 'light' ? alreadyLearnedIconLight : alreadyLearnedIconDark 
    },
    { 
      name: "Учу", 
      id: "learning", 
      icon: theme === 'light' ? learningIconLight : learningIconDark 
    },
    { 
      name: "Чс", 
      id: "blacklist", 
      icon: theme === 'light' ? blackListIconLight : blackListIconDark 
    }
  ];

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await fetchDictionaryCategories();
      if (response.success && response.data) {
        setCategories(response.data);
        setError(null);
      }
    } catch (err) {
      // Не отображаем ошибку пользователю
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    
    // Добавляем обработчик кликов для закрытия выпадающего списка
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchSectionRef.current && 
        !searchSectionRef.current.contains(event.target as Node)
      ) {
        setIsSearchFocused(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    if (value.trim()) {
      performSearch(value);
      if (!isSearchFocused) setIsSearchFocused(true);
    } else {
      setSearchResults([]);
    }
  };

  const performSearch = async (query: string, page: number = 0) => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    setError(null);
    
    try {
      const response = await searchWords(query.trim(), page);
      if (response.success && response.data) {
        const data = response.data as SearchResponse;
        if (Array.isArray(data.content) && data.content.length > 0) {
          // Обрабатываем формат, который приходит от сервера
          const mappedResults = data.content.map((item: any): WordItem => ({
            characters: item.word,
            pinyin: item.pinyin || '',
            translation: item.translation || '',
            priority: item.priority || 0,
            example: item.example,
            originalData: item
          }));
          
          setSearchResults(mappedResults);
          setTotalPages(data.totalPages);
          setTotalElements(data.totalElements);
          setCurrentPage(data.pageable.pageNumber);
        } else {
          setSearchResults([]);
          setTotalPages(0);
          setTotalElements(0);
          setCurrentPage(0);
        }
      } else {
        setError(response.error || 'Не удалось выполнить поиск');
        logger.error('Ошибка при поиске:', response.error);
      }
    } catch (err) {
      setError('Произошла ошибка при поиске');
      logger.error('Исключение при поиске:', err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      performSearch(searchQuery);
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };
  
  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };
  
  const handleWordListClick = () => {
    // Предотвращаем закрытие панели при клике на список результатов
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleCategoryClick = (category: WordCategory) => {
    navigate(`/dictionary/category/${category.name}?emoji=${encodeURIComponent(category.emoji || '📝')}`);
  };

  const handlePresetClick = (id: string) => {
    logger.log('Клик по пресету:', id);
    navigate(`/dictionary/category/${id}`);
  };

  const handleWordClick = (word: WordItem) => {
    logger.log('Выбрано слово:', word);
    const wordData: Word = {
      word: word.characters,
      pinyin: word.pinyin || '',
      translation: word.translation || '',
      priority: word.priority || 0,
      example: word.example
    };
    setSelectedWord(wordData);
    setIsSearchFocused(false);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  const handleWordDoubleClick = async (word: WordItem) => {
    logger.log('Двойной клик по слову:', word);
    
    const wordData: Word = {
      word: word.characters,
      pinyin: word.pinyin || '',
      translation: word.translation || '',
      priority: word.priority || 0,
      example: word.example
    };
    
    try {
      const response = await updateWordStatus(wordData.word, 'IN_PROGRESS');
      
      if (response.success) {
        logger.log(`Статус слова "${wordData.word}" изменен на IN_PROGRESS`);
        
        setHighlightedWords(prev => {
          const newSet = new Set(prev);
          newSet.add(word.characters);
          return newSet;
        });
      } else {
        logger.error(`Ошибка при изменении статуса слова: ${response.error}`);
      }
    } catch (error) {
      logger.error('Исключение при изменении статуса слова:', error);
    }
  };

  const handleCloseWordCard = () => {
    setSelectedWord(null);
  };

  const handleStatusChange = (status: WordStatus) => {
    logger.log(`Статус слова изменен на: ${status}`);
    setSelectedWord(null);
    
    if (status === 'IN_PROGRESS' && selectedWord) {
      setHighlightedWords(prev => {
        const newSet = new Set(prev);
        newSet.add(selectedWord.word);
        return newSet;
      });
    }
  };

  const openAddCategoryModal = () => {
    setIsAddCategoryModalOpen(true);
  };

  const closeAddCategoryModal = () => {
    setIsAddCategoryModalOpen(false);
  };

  const openGenerateCategoryModal = () => {
    setIsGenerateCategoryModalOpen(true);
  };

  const closeGenerateCategoryModal = () => {
    setIsGenerateCategoryModalOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < totalPages) {
      performSearch(searchQuery, newPage);
    }
  };

  // Определяем, нужно ли показывать выпадающий список
  const showSearchResults = isSearchFocused && searchQuery.trim() && 
    (searchResults.length > 0 || error);

  return (
    <div className="dictionary-page">
      <h1 className="dictionary-title">Словарь</h1>
      
      <div className="presets">
        <div className="presets-list">
          {presets.map((preset, index) => (
            <div 
              key={index} 
              className="preset-item"
              onClick={() => handlePresetClick(preset.id)}
            >
              <img src={preset.icon} alt={preset.name} className="preset-icon" />
              <span className="preset-name">{preset.name}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="search-section" ref={searchSectionRef}>
        <div className="search-container">
          <div className="search-wrapper">
            <img 
              src={theme === 'light' ? searchIconLight : searchIconDark} 
              alt="Поиск" 
              className="search-icon" 
            />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              onFocus={handleSearchFocus}
              placeholder="Поиск слов..."
              className="search-input"
            />
          </div>
        </div>
        
        {/* Выпадающая панель результатов */}
        {showSearchResults && (
          <div 
            className="search-results-dropdown"
            onClick={handleWordListClick}
          >
            {error ? (
              <div className="error-message">{error}</div>
            ) : searchResults.length > 0 ? (
              <>
                <p className="result-count">Найдено: {totalElements}</p>
                <WordsList 
                  words={searchResults} 
                  className="results-list"
                  itemClassName="result-item"
                  onWordClick={handleWordClick}
                  onWordDoubleClick={handleWordDoubleClick}
                  highlightedWords={highlightedWords}
                />
                {totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                    >
                      Назад
                    </button>
                    <span className="pagination-info">
                      Страница {currentPage + 1} из {totalPages}
                    </span>
                    <button 
                      className="pagination-button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages - 1}
                    >
                      Вперед
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
      
      <div className="word-categories">
        {error ? (
          <div className="error-message">{error}</div>
        ) : (
          <div className="categories-wrapper">
            <div className="categories-header">
              <h2>Списки</h2>
              <div className="header-buttons">
                <button 
                  className="generate-category-button"
                  onClick={openGenerateCategoryModal}
                >
                  <img 
                    src={theme === 'light' ? generateCategoryIconLight : generateCategoryIconDark} 
                    alt="Сгенерировать" 
                    width="36" 
                    height="36" 
                  />
                </button>
                <button 
                  className="add-category-button"
                  onClick={openAddCategoryModal}
                >
                  <img 
                    src={theme === 'light' ? addIconLight : addIconDark} 
                    alt="Добавить" 
                    width="36" 
                    height="36" 
                  />
                </button>
              </div>
            </div>
            <div className="categories-grid">
              {categories.map((category, index) => (
                <div 
                  key={index} 
                  className="category-item"
                  onClick={() => handleCategoryClick(category)}
                >
                  <span className="category-emoji">{category.emoji || '📝'}</span>
                  <span className="category-name">{category.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedWord && (
        <WordCard 
          word={selectedWord}
          onClose={handleCloseWordCard}
          showStatusButtons={true}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Модальное окно для добавления категории */}
      <CreateCategoryModal
        isOpen={isAddCategoryModalOpen}
        onClose={closeAddCategoryModal}
        onCategoryCreated={(categoryName) => {
          loadCategories();
        }}
      />

      <GenerateCategoryModal
        isOpen={isGenerateCategoryModalOpen}
        onClose={closeGenerateCategoryModal}
        onCategoryCreated={(categoryName) => {
          loadCategories();
        }}
      />
    </div>
  );
};

export default Dictionary;